import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, deleteDoc } from "firebase/firestore";
import Spinner from './Spinner.js';
import { ArrowLeft, Edit2, Copy, Trash2, Tag, Info, DollarSign, Image as ImageIcon } from 'lucide-react';

const ProductDetail = ({ db }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const docSnap = await getDoc(doc(db, "products", id));
        if (docSnap.exists()) {
          setProduct({ id: docSnap.id, ...docSnap.data() });
        } else {
          alert("Product not found");
          navigate('/admin/products');
        }
      } catch (error) {
        console.error("Error fetching product detail:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [db, id, navigate]);

  const handleClone = () => {
    navigate('/admin/products/new', { state: { cloneData: product } });
  };

  const handleDelete = async () => {
    if (window.confirm("Delete this product from master catalog?")) {
      try {
        await deleteDoc(doc(db, "products", id));
        navigate('/admin/products');
      } catch (error) {
        alert("Error: " + error.message);
      }
    }
  };

  if (loading) return <Spinner />;
  if (!product) return null;

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/admin/products')} style={backBtnStyle}>
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{product.name}</h1>
            <p style={{ color: '#64748b', margin: '4px 0 0 0' }}>Master Product Data</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate(`/admin/products/edit/${id}`)} style={{ ...adminBtnStyle, backgroundColor: '#2563eb', color: 'white', border: 'none' }}>
            <Edit2 size={18} /> Edit
          </button>
          <button onClick={handleClone} style={{ ...adminBtnStyle, backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db' }}>
            <Copy size={18} /> Clone
          </button>
          <button onClick={handleDelete} style={{ ...adminBtnStyle, backgroundColor: 'white', color: '#ef4444', border: '1px solid #fecaca' }}>
            <Trash2 size={18} /> Delete
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
        
        {/* MEDIA SECTION */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}><ImageIcon size={20} color="#2563eb" /> Product Image</div>
          <div style={{ padding: '20px', textAlign: 'center', minHeight: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {product.image ? (
              <img src={product.image} alt={product.name} style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }} />
            ) : (
              <div style={{ color: '#94a3b8' }}>No image URL provided</div>
            )}
          </div>
        </div>

        {/* IDENTITY SECTION */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}><Info size={20} color="#6366f1" /> Basic Info</div>
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <AttributeItem label="SKU" value={product.sku} bold />
            <AttributeItem label="Base SKU" value={product.basesku || '---'} />
            <AttributeItem label="SAC Code" value={product.sac || '---'} />
            <AttributeItem label="UOM" value={product.uom || 'nos'} />
            <AttributeItem label="Weight" value={product.weight ? `${product.weight} kg` : '---'} />
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
               <label style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 'bold' }}>Description</label>
               <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#374151', lineHeight: '1.5' }}>{product.description || 'No description provided'}</p>
            </div>
          </div>
        </div>

        {/* CLASSIFICATION */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}><Tag size={20} color="#10b981" /> Categorization</div>
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <AttributeItem label="Category" value={product.category} />
            <AttributeItem label="Type" value={product.type} />
            <AttributeItem label="Brand" value={product.brand} />
            <AttributeItem label="Manufacture" value={product.manufacture} />
          </div>
        </div>

        {/* FINANCIALS */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}><DollarSign size={20} color="#f59e0b" /> Financials</div>
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <MetricItem label="Rate" value={product.rate || 0} prefix="₹" />
            <MetricItem label="Landing Rate" value={product.landingRate || 0} prefix="₹" />
            <MetricItem label="Inventory Value" value={product.inventoryvalue || 0} prefix="₹" />
          </div>
        </div>

      </div>
    </div>
  );
};

const MetricItem = ({ label, value, prefix = '' }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ fontSize: '13px', color: '#64748b' }}>{label}</span>
    <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#111827' }}>{prefix}{Number(value).toLocaleString()}</span>
  </div>
);

const AttributeItem = ({ label, value, bold = false }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ fontSize: '12px', color: '#64748b' }}>{label}</span>
    <span style={{ fontSize: '14px', fontWeight: bold ? 'bold' : '600', color: '#374151' }}>{value}</span>
  </div>
);

const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const adminBtnStyle = { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' };
const cardStyle = { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' };
const cardHeaderStyle = { padding: '15px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#fdfdfd', fontWeight: 'bold', fontSize: '14px', color: '#334155' };

export default ProductDetail;
